import { mediator, messenger, PartyHasAlreadyJoinedError, procedures } from './index.js'
import { useAppStore } from 'app/src/stores/index.js'
import { useLocalStream } from 'app/src/composables/index.js'
import { repository } from './messages/index.js'
import { EventBus } from './EventBus.js'

const RECONNECT_DELAY = 3000
const MAX_RECONNECT_ATTEMPTS = 2000
const MAX_REJOIN_ATTEMPTS = 10 // con:join tries after a reconnect, RECONNECT_DELAY apart (about 27 s)

type MessageCallback = (error?: Error, result?: any) => void
class JsonRpc extends EventBus<any> {
  private ws?: WebSocket;
  private reconnectAttempts = 0;
  private pending: Map<number, {
    callback?: MessageCallback,
    timer: NodeJS.Timeout
  }> = new Map();
  private closing: boolean = false;
  private reconnecting: boolean = false;
  private status: 'CONNECTING' | 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED' | 'CLOSED' = 'CONNECTING'
  private reconnectTimer?: NodeJS.Timeout;
  private nextId: number = 0;
  constructor() {
    super()
    window.addEventListener('offline', this.onOffline);
    window.addEventListener('online', this.onOnline);
    if (navigator.onLine) {
      this._openSocket()
    }
  }
  _openSocket = (isReconnect: boolean = false) => {
    const windowObj = window as unknown as { __URI__: () => string }
    const ws = new WebSocket(windowObj.__URI__() + "jsonrpc")
    ws.onopen = () => this._handleOpen(ws, isReconnect)
    ws.onmessage = event => this._handleMessage(event)
    ws.onerror = () => this._handleError(ws)
    ws.onclose = () => this._handleClose(ws)
    this.ws = ws
    ws.binaryType = 'arraybuffer'
  }

  _handleOpen(ws: WebSocket, isReconnect: boolean) {
    if (ws !== this.ws) {
      return
    }
    this.reconnectAttempts = 0
    this.reconnecting = false
    if (isReconnect) {
      this.onReconnectedHandler()
    } else {
      this.onConnectedHandler()
    }
  }

  _handleMessage(event: MessageEvent) {
    messenger.emit('increase-ws-byte', event.data)
    let frame
    try {
      frame = JSON.parse(event.data)
    } catch (error) {
      console.error('Invalid JSON-RPC frame received', error)
      return
    }
    const pending = this.pending.get(frame.id)
    if (pending) {
      const { callback, timer } = pending
      clearTimeout(timer)
      this.pending.delete(frame.id)
      callback?.(frame.error || null, frame.result)
      return
    }

    if (frame.method) {
      this.emit(frame.method, frame.params)
    }
  }

  _handleError(ws: WebSocket) {
    if (ws !== this.ws) {
      return
    }
    // A close event always follows the error event; _handleClose schedules the retry.
    this.onErrorHandler()
  }

  _handleClose(ws: WebSocket) {
    if (ws !== this.ws) return
    // Scheduled first (no-op when closing), so an exception in the callbacks below cannot stop reconnecting.
    this._retryReconnect()
    this._rejectAllPending(new Error('WebSocket closed'))
    if (this.closing) {
      this.status = 'CLOSED'
      return
    }
    // Report the outage once, then only retry. A separate flag is used because the handlers
    // keep status 'DISCONNECTED' for the whole outage (ParticipantDisconnectedProcedure checks it).
    if (!this.reconnecting) {
      this.reconnecting = true
      this.onDisconnectedHandler()
      this.onReconnectingHandler()
    }
  }

  _retryReconnect() {
    if (this.closing) return
    this.reconnectAttempts++
    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      return
    }
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      if (this.closing) return
      this._openSocket(true)
    }, RECONNECT_DELAY)
  }

  _rejectAllPending(error: Error) {
    // Each entry is removed before its callback runs and a throwing callback is only logged, so one bad
    // callback cannot skip the others or the outage handling in _handleClose.
    for (const [id, { callback, timer }] of this.pending) {
      this.pending.delete(id)
      clearTimeout(timer)
      try {
        callback?.(error)
      } catch (callbackError) {
        console.error('Pending JSON-RPC callback failed', callbackError)
      }
    }
  }

  close() {
    this.closing = true
    clearTimeout(this.reconnectTimer)
    procedures.close()
    this.ws?.close()
  }


  send(message: Record<string, any>, callback?: MessageCallback, logMethod = 'no-log') {
    if (logMethod !== 'no-log') {
      console.info(`${message.method} => ${JSON.stringify(message.params)}`)
    }
    return this._send(message.method, message.params, callback)
  }

  _send(method: string, params: Record<string, any>, callback?: MessageCallback) {
    const id = this.nextId++
    const frame = { method, params: params ?? null, id }
    messenger.emit('increase-ws-byte', frame)

    if (callback) {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        callback(new Error(`Request timeout: ${method}`))
      }, 60000)
      this.pending.set(id, { callback, timer })
    }

    try {
      this.ws?.send(JSON.stringify(frame))
    } catch (error: any) {
      const pending = this.pending.get(id)
      if (callback && pending) {
        clearTimeout(pending.timer)
        this.pending.delete(id)
        callback(error)
      }
    }
  }
  sendRepositoryEvent = (message: {
    type: string
    data: any
  }) => {
    if (mediator.session) {
      const { party, id } = mediator.session.user
      const {
        service: { callId },
      } = mediator.session.configuration

      const namespace = message.type.split(':')[0]
      const { type } = message

      return this.send(
        repository.sendEventMessage({
          ...message,
          eventType: namespace,
          callId: callId,
          liteSessionId: mediator.session.callId,
          partyId: id,
          party,
          type,
        })
      )
    }
  }

  sendStats = (stats: Record<string, any>) => {
    if (mediator.session) {
      const {
        service: { callId },
      } = mediator.session.configuration
      const { callId: liteSessionId } = mediator.session
      return this.send(repository.statsMessage({ ...stats, liteSessionId, callId }))
    }
  }

  sendAndWait<R>(message: Record<string, any>): Promise<R> {
    console.info(`${message.method} => ${JSON.stringify(message.params)}`)
    return new Promise((resolve, reject) => {
      this._send(message.method, message.params, (error, result: R) => {
        if (error) {
          reject(error)
        } else {
          resolve(result)
        }
      })
    })
  }

  onConnectedHandler = () => {
    this.status = 'CONNECTED'
    messenger.emit('websocket:connected')
  }

  onDisconnectedHandler = () => {
    this.status = 'DISCONNECTED'
    messenger.emit('websocket:disconnected')
  }

  onReconnectingHandler = () => {
    this.status = 'DISCONNECTED'
    messenger.emit('websocket:reconnecting')
    const app = useAppStore()
    if (app.state.matches('app.view.meeting.lobby') || app.state.matches('app.view.meeting')) {
      app.transition('lobby')
      mediator.reset()
    }
  }

  onReconnectedHandler = async () => {
    this.status = 'CONNECTED'
    messenger.emit('websocket:reconnected')
    const app = useAppStore()

    if (app.state.matches('app.view.meeting.lobby') || app.state.matches('app.view.meeting')) {
      app.transition('meeting')
      const ws = this.ws
      try {
        // After a network loss the server keeps this party on the dead socket until its ping check drops it,
        // so PARTY_HAS_ALREADY_JOINED is retried for a while, as long as this socket stays connected
        for (let attempt = 1; ; attempt++) {
          try {
            await mediator.session?.join()
            break
          } catch (error) {
            if (!(error instanceof PartyHasAlreadyJoinedError) || attempt >= MAX_REJOIN_ATTEMPTS) throw error
          }
          await new Promise(resolve => setTimeout(resolve, RECONNECT_DELAY))
          if (ws !== this.ws || this.status !== 'CONNECTED' || !app.state.matches('app.view.meeting')) return
        }
        // TODO: handle strategy switch after reconnection
        // await mediator.call()
      } catch (error) {
        // Still held by another socket after all attempts, or the session no longer exists on the platform
        if (error instanceof PartyHasAlreadyJoinedError || (error as {message?: string})?.message === 'SESSION_NOT_FOUND') {
          const { stream } = useLocalStream()
          stream.video.stop()
          stream.audio.stop()
          app.transition('exit')
        } else if (ws === this.ws && ws?.readyState === WebSocket.OPEN) {
          // Any other re-join failure (e.g. a transient server error) would leave the meeting without a join:
          // drop the socket so the regular reconnect joins again
          console.info('Re-join failed, reconnecting', error)
          ws.close()
        }
      }
    }
  }

  onErrorHandler = () => {
    this.status = 'DISCONNECTED'
    console.info('WebSocket error occurred')
    messenger.emit('websocket:error')
  }

  onOffline = () => {
    // A lost Wi-Fi often leaves the socket OPEN (half-open TCP) with no close event, so the outage was
    // never reported and nothing reconnected. Close it now and run the regular close path (reject pending,
    // report the outage once, schedule the retry); its own late events are detached so they cannot run it again.
    const ws = this.ws
    if (this.closing || !ws || ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED) {
      return
    }
    ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
    ws.close()
    this._handleClose(ws)
  }
  onOnline = () => {
    // Connect now instead of waiting for the retry timer. This also opens the first socket
    // when the page was loaded offline. A CLOSED socket is replaced only during an outage that
    // was already reported; any other socket is left alone (its close event reports the outage
    // and schedules the retry).
    if (this.closing || (this.ws && !(this.reconnecting && this.ws.readyState === WebSocket.CLOSED))) {
      return
    }
    clearTimeout(this.reconnectTimer)
    this._openSocket(this.reconnecting)
  }

  sendBinary = (data: BufferSource) => {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data)
    }
  }
}

const jsonRpc = new JsonRpc()
export { jsonRpc }
